import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface RoadmapRow {
  readonly roadmapId: string;
  readonly roadmapCode: string;
  readonly roadmapName: string;
  readonly roadmapType: string;
  readonly versionNo: number;
  readonly approvalLevel: string | null;
  readonly status: string;
}

const TYPES = ['', 'QUARTERLY', 'HALF_YEARLY', 'ANNUAL', 'STRATEGIC', 'TECHNOLOGY', 'COMPLIANCE'];
const STATUSES = ['', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M33 — Roadmap Planner (§31 / §16). Register of delivery roadmaps (T442) with
 * horizon type, version number, executive approval level and the draft→pending→
 * approve→publish lifecycle (BR-05 requires executive approval before publish).
 * Supports authoring a roadmap and driving it — submit, approve, publish and
 * archive — each wired to the guarded /v1/pm/roadmaps write API. Filterable by
 * roadmap type and status.
 */
export function RoadmapPlanner(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RoadmapRow[]>([]);
  const [roadmapType, setRoadmapType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('QUARTERLY');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (roadmapType) params.set('roadmapType', roadmapType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: RoadmapRow[] }>(`/v1/pm/roadmaps${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, roadmapType, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(t('m33.roadmap.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/roadmaps`, {
        method: 'POST',
        body: { roadmapName: newName.trim(), roadmapType: newType },
        token,
      });
      setNotice(t('m33.roadmap.created', { name: newName.trim() }));
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'ROADMAP_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: RoadmapRow, path: string, label: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/roadmaps/${r.roadmapId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m33.common.notice', { code: r.roadmapCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="roadmap-planner">
      <h1>{t('m33.roadmap.title')}</h1>

      <section aria-label="create-roadmap">
        <h2>{t('m33.roadmap.createHeading')}</h2>
        <form aria-label="roadmap-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.name')}<input aria-label="field-roadmap-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{t('m33.common.type')}
            <select aria-label="field-roadmap-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-roadmap" disabled={busy}>{busy ? t('m33.roadmap.creating') : t('m33.roadmap.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="roadmap-error">{actionError}</p>}
        {notice && <p aria-label="roadmap-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.type')}
          <select aria-label="filter-roadmap-type" value={roadmapType} onChange={(e) => setRoadmapType(e.target.value)}>
            {TYPES.map((ty) => <option key={ty || 'all'} value={ty}>{ty || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="roadmap-count">{t('m33.roadmap.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="roadmap-table"
            rows={rows}
            rowKey={(r) => r.roadmapId}
            columns={[
              { key: 'roadmapCode', header: t('m33.common.code'), render: (r) => r.roadmapCode },
              { key: 'roadmapName', header: t('m33.common.name'), render: (r) => r.roadmapName },
              { key: 'roadmapType', header: t('m33.common.type'), render: (r) => r.roadmapType },
              { key: 'versionNo', header: t('m33.common.version'), render: (r) => <>v{r.versionNo}</> },
              { key: 'approvalLevel', header: t('m33.roadmap.colApproval'), render: (r) => r.approvalLevel ?? '—' },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.roadmapCode}`} onClick={() => void act(r, 'submit', t('m33.roadmap.act.submitted'), 'ROADMAP_MANAGE')}>{t('m33.roadmap.btnSubmit')}</Button>
                  )}
                  {r.status === 'PENDING_APPROVAL' && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.roadmapCode}`} onClick={() => void act(r, 'approve', t('m33.roadmap.act.approved'), 'ROADMAP_APPROVE')}>{t('m33.roadmap.btnApprove')}</Button>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.roadmapCode}`} onClick={() => void act(r, 'publish', t('m33.roadmap.act.published'), 'ROADMAP_PUBLISH')}>{t('m33.roadmap.btnPublish')}</Button>
                  )}
                  {r.status !== 'ARCHIVED' && (
                    <Button variant="danger" type="button" aria-label={`archive-${r.roadmapCode}`} onClick={() => void act(r, 'archive', t('m33.roadmap.act.archived'), 'ROADMAP_MANAGE')}>{t('m33.roadmap.btnArchive')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
